// ISDP CUSTOM: `ui:field: AuthorProfile` — the doc author's name + email, prefilled from the signed-in
// DevHub profile (Okta displayName + email), both editable. Value: { name, email }.
import { useEffect, useRef, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import type { FieldExtensionComponentProps, CustomFieldValidator } from '@backstage/plugin-scaffolder-react';
import { FormControl, FormHelperText, FormLabel, TextField } from '@material-ui/core';
import { devsecopsMessages } from '../../../i18n/devsecops';
import {
  authorProfileErrors,
  prefillAuthorProfile,
  type AuthorProfileError,
  type AuthorProfileValue,
} from './fieldHelpers';
import { validatorMessages, type FieldMessageKey } from './fieldMessages';

export const AUTHOR_ERROR_KEYS: Record<AuthorProfileError, FieldMessageKey> = {
  nameRequired: 'fields.authorNameRequired',
  emailRequired: 'fields.authorEmailRequired',
  emailInvalid: 'fields.authorEmailInvalid',
};

export const AuthorProfileField = (props: FieldExtensionComponentProps<AuthorProfileValue>) => {
  const { formData, onChange, rawErrors, required, disabled, schema, idSchema, uiSchema } = props;
  const identityApi = useApi(identityApiRef);
  const { t } = useTranslationRef(devsecopsMessages);
  const tt = t as unknown as (k: string) => string;
  const value: AuthorProfileValue = { name: formData?.name ?? '', email: formData?.email ?? '' };
  const latest = useRef(value);
  latest.current = value;
  const [touched, setTouched] = useState({ name: false, email: false });

  // Prefill once, on first render, without overwriting anything already typed (or restored).
  useEffect(() => {
    let active = true;
    identityApi
      .getProfileInfo()
      .then(profile => {
        if (!active) return;
        const next = prefillAuthorProfile(latest.current, profile);
        if (next.name !== latest.current.name || next.email !== latest.current.email) onChange(next);
      })
      .catch(() => { /* no profile — leave the inputs empty for the user */ });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityApi]);

  // Show a sub-field's error once it was touched, or once Next reported errors for this field.
  const showAll = (rawErrors?.length ?? 0) > 0;
  const errors = authorProfileErrors(value);
  const nameErr = errors.find(e => e === 'nameRequired');
  const emailErr = errors.find(e => e === 'emailRequired' || e === 'emailInvalid');
  const id = idSchema?.$id ?? 'authorProfile';
  const description = (uiSchema?.['ui:description'] as string | undefined) ?? schema.description ?? tt('fields.authorHint');

  return (
    <FormControl component="fieldset" fullWidth margin="normal" required={required} className="devhub-sf">
      {schema.title && <FormLabel component="legend">{schema.title}</FormLabel>}
      <div className="devhub-sf__row">
        <TextField
          id={`${id}-name`}
          label={tt('fields.authorName')}
          value={value.name}
          required
          disabled={disabled}
          autoComplete="name"
          fullWidth
          margin="dense"
          error={!!nameErr && (touched.name || showAll)}
          helperText={nameErr && (touched.name || showAll) ? tt(AUTHOR_ERROR_KEYS[nameErr]) : ' '}
          onChange={e => onChange({ ...latest.current, name: e.target.value })}
          onBlur={() => setTouched(s => ({ ...s, name: true }))}
        />
        <TextField
          id={`${id}-email`}
          label={tt('fields.authorEmail')}
          type="email"
          value={value.email}
          required
          disabled={disabled}
          autoComplete="email"
          fullWidth
          margin="dense"
          error={!!emailErr && (touched.email || showAll)}
          helperText={emailErr && (touched.email || showAll) ? tt(AUTHOR_ERROR_KEYS[emailErr]) : ' '}
          onChange={e => onChange({ ...latest.current, email: e.target.value })}
          onBlur={() => setTouched(s => ({ ...s, email: true }))}
        />
      </div>
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
};

export const authorProfileValidation: CustomFieldValidator<AuthorProfileValue> = (value, validation, { apiHolder }) => {
  const msg = validatorMessages(apiHolder);
  for (const err of authorProfileErrors(value)) validation.addError(msg(AUTHOR_ERROR_KEYS[err]));
};
